import { memo, useEffect, useRef, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { classNames } from 'primereact/utils';
import { CloseIcon } from '@repo/atomic-components';

interface SearchablePopupProps {
  /** Position of the popup relative to viewport */
  position: { top: number; left: number };
  /** Called when user clicks outside or presses Escape */
  onClose: () => void;
  /** Search input value */
  searchQuery: string;
  /** Called when search input changes */
  onSearchChange: (value: string) => void;
  /** Placeholder text for search input */
  searchPlaceholder: string;
  /** Currently focused item index (-1 for create button, 0+ for list items) */
  focusedIndex: number;
  /** Called when focused index should change */
  onFocusedIndexChange: (index: number) => void;
  /** Total number of items in the list (for keyboard nav wrapping) */
  itemCount: number;
  /** Minimum focusable index (usually -1 if create button, else 0) */
  minFocusIndex?: number;
  /** Called when Enter is pressed on the focused item */
  onEnterPress: () => void;
  /** Optional additional keyboard handler */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Width of the popup (CSS value) */
  width?: string;
  /** Popup contents (search bar, list, etc.) */
  children: ReactNode;
  /** Auto-focus search input on mount */
  autoFocusSearch?: boolean;
}

/**
 * Reusable popup shell for searchable lists with keyboard navigation.
 * Provides: portal rendering, positioning, click-outside handling,
 * search input with clear button, and arrow key navigation.
 */
export const SearchablePopup: React.FC<SearchablePopupProps> = memo(
  ({
    position,
    onClose,
    searchQuery,
    onSearchChange,
    searchPlaceholder,
    focusedIndex,
    onFocusedIndexChange,
    itemCount,
    minFocusIndex = 0,
    onEnterPress,
    onKeyDown,
    width = '280px',
    children,
    autoFocusSearch = true,
  }) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Auto-focus search input
    useEffect(() => {
      if (autoFocusSearch && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [autoFocusSearch]);

    // Handle clicks outside the popover
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (popoverRef.current && !popoverRef.current.contains(target)) {
          onClose();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [onClose]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Allow parent to handle keyboard events first
      if (onKeyDown) {
        onKeyDown(e);
        if (e.defaultPrevented) return;
      }

      if (itemCount === 0 && minFocusIndex >= 0) return;

      const maxIndex = itemCount - 1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (focusedIndex < maxIndex) {
            onFocusedIndexChange(focusedIndex + 1);
          } else {
            onFocusedIndexChange(minFocusIndex); // Wrap to top
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (focusedIndex > minFocusIndex) {
            onFocusedIndexChange(focusedIndex - 1);
          } else {
            onFocusedIndexChange(maxIndex); // Wrap to bottom
          }
          break;
        case 'Enter':
          e.preventDefault();
          onEnterPress();
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    return ReactDOM.createPortal(
      <div
        ref={popoverRef}
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          zIndex: 9999,
        }}
      >
        <div
          className="flex flex-col rounded-lg border bg-white"
          style={{
            width,
            maxHeight: '400px',
            border: '1px solid #E1E1E0',
            boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.25)',
          }}
        >
          {/* Header with search */}
          <div className="border-fletchers-gray-110 flex flex-col gap-3 border-b px-3 py-3">
            <div className="relative">
              <i className="pi pi-search absolute top-1/2 left-[10px] -translate-y-1/2 text-[14px] text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                className={classNames(
                  'border-fletchers-gray-110 h-[32px] w-full rounded-[4px] border bg-white pr-8 pl-[35px] text-[14px] leading-[20px] text-black',
                  'placeholder:text-fletchers-gray-200 focus:outline-none',
                )}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 focus:outline-none"
                  onClick={() => onSearchChange('')}
                >
                  <CloseIcon width={10} height={10} />
                </button>
              )}
            </div>
          </div>

          {/* Content area (list, create mode, etc.) */}
          {children}
        </div>
      </div>,
      document.body,
    );
  },
);
